import { Stack, Text, Center } from '@mantine/core';
import { CalendarDays } from 'lucide-react';
import { SessionsToolbar } from './SessionsToolbar';
import { SessionsTable } from './SessionsTable';
import { useDataLibrarySessions } from './useDataLibrarySessions';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';

interface DataLibrarySessionsTabProps {
  selectedCohortIds: string[];
  /** MED-661: Initial filters when opened from drill-down (e.g. session number) */
  initialFilters?: ActiveFilterRow[];
}

export function DataLibrarySessionsTab({
  selectedCohortIds,
  initialFilters,
}: DataLibrarySessionsTabProps) {
  const {
    data,
    groupedData,
    loading,
    totalCount,
    averages,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize,
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    activeGroup,
    setActiveGroup,
    columns,
    setColumns,
    visibleColumns,
    groupLimitTotal,
  } = useDataLibrarySessions({ selectedCohortIds, initialFilters });

  if (selectedCohortIds.length === 0) {
    return (
      <Center h={300}>
        <Stack align="center" gap="sm">
          <CalendarDays size={40} color="#888" />
          <Text c="dimmed" size="lg">
            Select one or more cohorts to view sessions
          </Text>
          <Text c="dimmed" size="sm">
            Use the side panel to choose cohorts
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="sm">
      <SessionsToolbar
        groupLimitTotal={groupLimitTotal}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        filterOptions={filterOptions}
        activeFilters={activeFilters}
        onFilterAdd={handleFilterAdd}
        onFilterChange={handleFilterChange}
        onFilterRemove={handleFilterRemove}
        onClearAll={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
        sortConfig={sortConfig}
        onSortChange={handleSortPanelChange}
        activeGroup={activeGroup}
        onGroupChange={setActiveGroup}
        columns={columns}
        onColumnsChange={setColumns}
        totalCount={totalCount}
        selectedCohortIds={selectedCohortIds}
      />

      <SessionsTable
        data={data}
        averages={averages}
        groupedData={groupedData}
        columns={visibleColumns}
        sortConfig={sortConfig}
        onSort={handleSort}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={clearAllFilters}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        hidePagination={!!groupedData}
      />
    </Stack>
  );
}
